import {
  CheckCircle2,
  Clock,
  CreditCard,
  Package,
  PackageCheck,
  Truck,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

import { CardStatus } from "../../types/card";
import { PhysicalCardShippingStatus } from "../types/physical-card";

interface PhysicalCardShippingStepsProps {
  deliveryStatus: PhysicalCardShippingStatus;
}

interface StatusStep {
  key: PhysicalCardShippingStatus | CardStatus;
  label: string;
  icon: React.ReactNode;
  description?: string;
}

export const PhysicalCardShippingSteps = (
  props: PhysicalCardShippingStepsProps
) => {
  const { deliveryStatus } = props;
  const { t } = useTranslation();

  const statusSteps: StatusStep[] = [
    {
      key: PhysicalCardShippingStatus.NEW,
      label: t("physicalCard_status_ordered"),
      icon: <Package className="size-5" />,
    },
    {
      key: PhysicalCardShippingStatus.PENDING,
      label: t("physicalCard_status_shipped"),
      icon: <Truck className="size-5" />,
    },
    {
      key: PhysicalCardShippingStatus.SHIPPED,
      label: t("physicalCard_status_delivered"),
      icon: <PackageCheck className="size-5" />,
    },
    {
      key: CardStatus.ACTIVE,
      label: t("physicalCard_status_activated"),
      icon: <CreditCard className="size-5" />,
    },
  ];

  const getStatusIndex = (status: PhysicalCardShippingStatus): number => {
    if (status === PhysicalCardShippingStatus.NEW) {
      return 0;
    }
    if (status === PhysicalCardShippingStatus.PENDING) {
      return 1;
    }
    return statusSteps.findIndex((step) => step.key === status);
  };

  const currentIndex = getStatusIndex(deliveryStatus);

  return (
    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-y-6 md:gap-y-0 w-full">
      {statusSteps.map((step, index) => {
        const isCompleted = index < currentIndex;
        const isCurrent = index === currentIndex;
        const isPending = index > currentIndex;

        return (
          <div
            key={step.key}
            className="flex flex-row md:flex-col items-center flex-1 w-full md:w-auto"
          >
            <div className="relative flex flex-col md:flex-row items-center justify-center md:w-full">
              {/* Vertical line for mobile */}
              {index > 0 && (
                <div
                  className={cn(
                    "absolute -top-6 left-5 w-0.5 h-6 md:hidden",
                    isCompleted || isCurrent ? "bg-primary" : "bg-gray-200"
                  )}
                />
              )}

              {/* Horizontal line for desktop (Left) */}
              {index > 0 && (
                <div
                  className={cn(
                    "hidden md:block absolute left-0 right-1/2 h-0.5 top-1/2 -translate-y-1/2",
                    isCompleted || isCurrent ? "bg-primary" : "bg-gray-200"
                  )}
                />
              )}

              <div
                className={cn(
                  "relative z-10 flex items-center justify-center rounded-full size-10 transition-colors shrink-0",
                  isCompleted && "bg-primary text-white",
                  isCurrent && "bg-primary text-white ring-4 ring-primary/20",
                  isPending &&
                    "bg-gray-100 text-gray-400 border border-gray-200"
                )}
              >
                {isCompleted ? (
                  <CheckCircle2 className="size-5" />
                ) : isCurrent &&
                  deliveryStatus === PhysicalCardShippingStatus.PENDING ? (
                  <Clock className="size-5" />
                ) : (
                  step.icon
                )}
              </div>

              {/* Horizontal line for desktop (Right) */}
              {index < statusSteps.length - 1 && (
                <div
                  className={cn(
                    "hidden md:block absolute left-1/2 right-0 h-0.5 top-1/2 -translate-y-1/2",
                    isCompleted ? "bg-primary" : "bg-gray-200"
                  )}
                />
              )}
            </div>

            <span
              className={cn(
                "text-xs ml-4 md:ml-0 md:mt-2 text-left md:text-center md:max-w-[100px] wrap-break-word",
                isCompleted || isCurrent
                  ? "text-primary font-medium"
                  : "text-gray-400"
              )}
            >
              {step.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};
